/** biome-ignore-all lint/nursery/noExcessiveLinesPerFile: TODO: shorten this file on CD */
import { useQueryClient } from "@tanstack/react-query";
import classnames from "classnames";
import { isNull } from "lodash";
import { ArrowRight, Check, Trash2, X } from "lucide-react";
import { motion, type Variants } from "motion/react";
import { Fragment, type ReactNode, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { capitalizeWithSpaces, FormatNumber, formatTime, timeTo } from "utils";

import { Button } from "@krea/shared/design-system/Button";
import { ResponsiveTooltip } from "@krea/shared/design-system/ResponsiveTooltip";
import { useFormatCurrency } from "@krea/shared/hooks/useFormatCurrency";
import { CountryCode } from "@krea/shared/types/common";

import {
  LenderApplicationsFilterEnum,
  LenderApplicationTypeEnum,
} from "@/features/common/enums";
import type { useGetLenderApplicationListQuery } from "@/features/lender-applications/hooks/data/use-get-lender-application-list.query";
import type { ILenderApplicationMinimal } from "@/features/lender-applications/types/lender-application";
import type { IFieldRequest } from "@/features/lender-responses/types/field-request";
import { queryKeyMap } from "@/features/query-client";
import { FIELD_REQUESTS_NAMES } from "@/utils/fieldRequests/fieldRequests";

import { AssignLenderUserSelect } from "../../assign-lender-user-select";
import { HighlightedCustomerSegmentIndicator } from "../../highlighted-customer-segment-indicator";
import RejectApplication from "../../modals/reject-application-modal";
import { Indicator } from "../indicator/index";

const GUARANTORS = [FIELD_REQUESTS_NAMES.GUARANTORS_FIELD];
const SECURITIES = [
  FIELD_REQUESTS_NAMES.PROPERTY_FIELD,
  FIELD_REQUESTS_NAMES.COMPANY_MORTGAGE_FIELD,
  FIELD_REQUESTS_NAMES.UNPAID_INVOICES_FIELD,
  FIELD_REQUESTS_NAMES.LEASING_FIELD,
];
const FINANCIAL = [
  FIELD_REQUESTS_NAMES.SELF_ESTIMATED_HISTORIC_REVENUE_FIELD,
  FIELD_REQUESTS_NAMES.SELF_ESTIMATED_REVENUE_FIELD,
  FIELD_REQUESTS_NAMES.ACCOUNTING_DATA_FIELD,
  FIELD_REQUESTS_NAMES.BANKING_DATA_FIELD,
];
const ADDITIONAL = [
  FIELD_REQUESTS_NAMES.BUDGET_FIELD,
  FIELD_REQUESTS_NAMES.ADDITIONAL_INFORMATION_FIELD,
  FIELD_REQUESTS_NAMES.ADDITIONAL_SECURITIES_FIELD,
];

const ucFirst = (str: string) => {
  if (!str || !str[0]) return str;

  return str[0].toUpperCase() + str.slice(1);
};

const InfoMarking = ({
  className,
  info,
  fieldRequests,
}: {
  className?: string;
  info?: boolean;
  fieldRequests?: IFieldRequest[];
}) => {
  const content = (
    <div>
      {info ? <Check className="size-4" /> : <X className="size-4" />}
    </div>
  );

  return (
    <td
      className={classnames(className, {
        "text-green-600": info,
        "text-red-500": !info,
      })}
    >
      {fieldRequests ? (
        <ResponsiveTooltip trigger={content} className="max-w-xs">
          {fieldRequests.map((request) => (
            <div key={request.id} className="py-0.5">
              {ucFirst(request.field.replace(/[_]+/g, " "))} -{" "}
              {request.isSubmitted ? (
                <Check className="inline size-4" />
              ) : (
                <X className="inline size-4" />
              )}
            </div>
          ))}
        </ResponsiveTooltip>
      ) : (
        content
      )}
    </td>
  );
};

const ListItem = ({
  className,
  title,
  value,
}: {
  className: string;
  title: string;
  value: ReactNode;
}) => {
  return (
    <dl
      className={classnames(
        "flex w-full items-baseline justify-between gap-4",
        className,
      )}
    >
      <dt className="m-0 shrink-0 font-medium text-gray-900">
        {title}:
      </dt>
      <dd className="m-0 flex min-w-0 flex-1 justify-end text-right text-gray-700">
        {value}
      </dd>
    </dl>
  );
};

const variants: Variants = {
  initial: {
    opacity: 0,
    x: -100,
  },
  visible: (custom: number) => ({
    opacity: 1,
    x: 0,
    transition: {
      delay: custom * 0.05,
      type: "tween",
      duration: 0.2,
      ease: "easeOut",
    },
  }),
};

export const Item = ({
  lenderApplication,
  mobile,
  filter,
  animationIndex,
  rowIndex,
}: {
  lenderApplication: ILenderApplicationMinimal;
  mobile: boolean;
  filter: LenderApplicationsFilterEnum;
  animationIndex: number;
  rowIndex?: number;
}) => {
  const navigate = useNavigate();
  const { loanApplication } = lenderApplication;
  const countryCode = loanApplication.countryCode;
  const dateReceived = formatTime(lenderApplication.markedEligibleForLenderAt);
  const timeToResult = timeTo(loanApplication.biddingEndsAt);
  const dateBiddingEnds = timeToResult.restTime;
  const paidOutAt = formatTime(loanApplication.payoutMarkedAsReceivedAt);
  let bidSelectedAt = "";

  if (
    lenderApplication.type ===
    LenderApplicationTypeEnum.THIS_LENDER_APPLICATION_BID_SELECTED
  ) {
    bidSelectedAt = formatTime(loanApplication.bidSelectedAt);
  }

  const timeWarning = timeToResult.warning;
  const { applyingCompany, fieldRequests } = loanApplication;
  const { customerCompany } = applyingCompany;

  const guarantorsFieldRequests = fieldRequests.filter((r) =>
    GUARANTORS.includes(r.field),
  );
  const securityFieldRequests = fieldRequests.filter((r) =>
    SECURITIES.includes(r.field),
  );
  const financialFieldRequests = fieldRequests.filter((r) =>
    FINANCIAL.includes(r.field),
  );
  const additionalFieldRequests = fieldRequests.filter((r) =>
    ADDITIONAL.includes(r.field),
  );

  const isGuarantorSubmitted =
    guarantorsFieldRequests.filter((r) => r.isSubmitted).length > 0;
  const isSecuritySubmitted =
    securityFieldRequests.filter((r) => r.isSubmitted).length > 0;
  const isFinancialSubmitted =
    financialFieldRequests.filter((r) => r.isSubmitted).length > 0;
  const isAdditionalSubmitted =
    additionalFieldRequests.filter((r) => r.isSubmitted).length > 0;

  const MobileInfo = (
    guarantor: boolean,
    financialInfo: boolean,
    securities: boolean,
    otherInfo: boolean,
  ) => {
    const info = [];

    if (guarantor) {
      info.push("Guarantor");
    }

    if (financialInfo) {
      info.push("Financial information");
    }

    if (securities) {
      info.push("Securities");
    }

    if (otherInfo) {
      info.push("Other info");
    }

    return info.length > 0 ? info.join(", ") : "No information";
  };

  const { id: lenderApplicationId } = lenderApplication;
  const {
    requestedLoanAmount,
    requestedPaybackTimeMonths,
    calculatedRevenueLastYear,
  } = loanApplication;
  const { creditScoreColor } = applyingCompany;
  const { name: companyName, organisationNumber } = customerCompany;

  const companyType = customerCompany.type
    ? customerCompany.type.replace(/[_]+/g, " ").toLowerCase()
    : "";

  const [formattedCurrencyValue] = useFormatCurrency({
    currency: loanApplication.currency,
  });

  const goToDetails = () => {
    navigate(`lender-application/${lenderApplicationId}`, {
      state: {
        from: {
          filter,
        },
      },
    });
  };

  const [showRejectModal, setShowRejectModal] = useState(false);

  const queryClient = useQueryClient();
  const optimisticUpdate = useCallback(
    (newApplication: ILenderApplicationMinimal | undefined) => {
      if (!newApplication) return;

      const queries = queryClient.getQueriesData<
        ReturnType<typeof useGetLenderApplicationListQuery>["data"]
      >({
        queryKey: queryKeyMap.getLenderApplications(filter),
        exact: false,
        type: "active",
      });

      queries.forEach(([queryKey]) => {
        queryClient.setQueryData<
          ReturnType<typeof useGetLenderApplicationListQuery>["data"]
        >(queryKey, (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page) => {
              return {
                ...page,
                items: page.items.map((item) =>
                  item.id === lenderApplicationId ? newApplication : item,
                ),
              };
            }),
          };
        });
      });
    },
    [filter, lenderApplicationId, queryClient],
  );

  return (
    <Fragment>
      {!mobile ? (
        <motion.tr
          key={lenderApplication.id}
          custom={animationIndex}
          initial="initial"
          animate="visible"
          variants={variants}
          className={classnames(
            "border-y border-gray-200 hover:!bg-primary-100",
            rowIndex !== undefined && rowIndex % 2 === 1 && "bg-gray-50",
          )}
        >
          <td className="relative">
            {lenderApplication.isHighlightedCustomerSegment && (
              <HighlightedCustomerSegmentIndicator
                className={"absolute left-0 top-0"}
              />
            )}
            <Button
              variant="link"
              className="p-0 !text-sm"
              onClick={goToDetails}
            >
              {lenderApplicationId}
            </Button>
          </td>

          <td className="whitespace-nowrap">
            {capitalizeWithSpaces(lenderApplication.product)}
          </td>

          {![
            LenderApplicationsFilterEnum.PAID_OUT,
            LenderApplicationsFilterEnum.BID_SELECTED,
          ].includes(filter) && (
            <td className="text-nowrap">{dateReceived}</td>
          )}

          {[
            LenderApplicationsFilterEnum.NO_COMPANY_LOAN_BIDS,
            LenderApplicationsFilterEnum.NO_INVOICE_PURCHASE_BIDS,
            LenderApplicationsFilterEnum.NO_REAL_ESTATE_BIDS,
          ].includes(filter) && (
            <td
              className={classnames("text-nowrap", {
                "text-red-500": timeWarning,
              })}
            >
              {dateBiddingEnds}
            </td>
          )}

          {filter === LenderApplicationsFilterEnum.PAID_OUT && (
            <td className="text-nowrap">{paidOutAt}</td>
          )}

          {[LenderApplicationsFilterEnum.BID_SELECTED].includes(filter) && (
            <td className="text-nowrap">{bidSelectedAt}</td>
          )}

          <td>{formattedCurrencyValue(requestedLoanAmount)}</td>

          <td>{requestedPaybackTimeMonths}</td>

          <td>{companyName || "-"}</td>

          <td>{ucFirst(companyType)}</td>

          <td className="text-nowrap">{organisationNumber}</td>

          <td className="text-nowrap">
            {countryCode === CountryCode.FI &&
            customerCompany.type === "SOLE_TRADER" ? null : (
              <Indicator risk={creditScoreColor} />
            )}
          </td>
          {calculatedRevenueLastYear ? (
            <td className="text-nowrap">
              {FormatNumber(calculatedRevenueLastYear)}
            </td>
          ) : (
            <InfoMarking className="text-nowrap" />
          )}
          <InfoMarking
            className="text-center"
            info={isGuarantorSubmitted}
            fieldRequests={guarantorsFieldRequests}
          />
          {/*

          <InfoMarking
            className="text-center"
            info={isFinancialSubmitted}
            fieldRequests={financialFieldRequests}
          />

          <InfoMarking
            className="text-center"
            info={isSecuritySubmitted}
            fieldRequests={securityFieldRequests}
          />

          <InfoMarking
            className="text-center"
            info={isAdditionalSubmitted}
            fieldRequests={additionalFieldRequests}
          /> */}

          <td>
            <AssignLenderUserSelect
              lenderApplicationId={lenderApplicationId}
              assignedLenderUser={lenderApplication.assignedLenderUser}
              className="w-28"
              onSuccess={optimisticUpdate}
            />
          </td>

          <td>
            <div className="flex justify-center gap-2">
              <ResponsiveTooltip
                trigger={
                  <Button onClick={goToDetails}>
                    <ArrowRight className="size-4" />
                  </Button>
                }
              >
                View Details
              </ResponsiveTooltip>

              {[
                LenderApplicationsFilterEnum.NO_COMPANY_LOAN_BIDS,
                LenderApplicationsFilterEnum.NO_INVOICE_PURCHASE_BIDS,
                LenderApplicationsFilterEnum.NO_REAL_ESTATE_BIDS,
                LenderApplicationsFilterEnum.NO_LEASING_BIDS,
              ].includes(filter) && (
                <ResponsiveTooltip
                  trigger={
                    <Button
                      variant="danger"
                      onClick={() => setShowRejectModal(true)}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  }
                >
                  Reject
                </ResponsiveTooltip>
              )}
            </div>
          </td>
        </motion.tr>
      ) : (
        <motion.section
          custom={animationIndex}
          initial="initial"
          animate="visible"
          variants={variants}
          className=" p-4 shadow-sm"
        >
          <div className="flex justify-between pb-3 text-gray-500">
            {filter === LenderApplicationsFilterEnum.PAID_OUT &&
              `Paid out time: ${paidOutAt}`}

            {[LenderApplicationsFilterEnum.BID_SELECTED].includes(filter) &&
              `Bid selection time: ${bidSelectedAt}`}

            {![
              LenderApplicationsFilterEnum.PAID_OUT,
              LenderApplicationsFilterEnum.BID_SELECTED,
            ].includes(filter) && dateReceived}

            {[
              LenderApplicationsFilterEnum.NO_COMPANY_LOAN_BIDS,
              LenderApplicationsFilterEnum.NO_INVOICE_PURCHASE_BIDS,
              LenderApplicationsFilterEnum.NO_REAL_ESTATE_BIDS,
            ].includes(filter) && (
              <dl
                className={classnames(
                  "m-0 flex flex-wrap justify-end",
                  { "text-red-500": timeWarning },
                )}
              >
                <dt className="m-0 shrink-0">Time left: </dt>
                <dd className="mb-0 ml-1 shrink-0">
                  {dateBiddingEnds}
                </dd>
              </dl>
            )}
          </div>

          <div className="mb-3 flex w-full items-start justify-between gap-4">
            <div className="shrink-0 text-base font-bold text-gray-900">
              {`#${lenderApplicationId}`}
            </div>
            <div className="flex min-w-0 flex-1 items-center justify-end gap-2 text-right text-base font-bold text-gray-900">
              <span>{`${companyName} (${organisationNumber})`}</span>
              {lenderApplication.isHighlightedCustomerSegment && (
                <HighlightedCustomerSegmentIndicator className="shrink-0" />
              )}
            </div>
          </div>

          <div className="w-full space-y-2 pb-4">
            <ListItem
              className="m-0"
              title="Request"
              value={`${formattedCurrencyValue(requestedLoanAmount)} / ${requestedPaybackTimeMonths} months`}
            />

            <ListItem
              className="m-0"
              title="Type"
              value={ucFirst(companyType)}
            />

            {countryCode === CountryCode.FI &&
            customerCompany.type === "SOLE_TRADER" ? null : (
              <ListItem
                className="m-0 items-center"
                title="Credit Score"
                value={<Indicator risk={creditScoreColor} />}
              />
            )}

            <ListItem
              className="m-0"
              title="Est. revenue last 12 months"
              value={
                !isNull(calculatedRevenueLastYear) ? (
                  formattedCurrencyValue(calculatedRevenueLastYear)
                ) : (
                  <div className="text-center">
                    <X className="size-4" />
                  </div>
                )
              }
            />

            <ListItem
              className="m-0"
              title="Info"
              value={MobileInfo(
                isGuarantorSubmitted,
                isFinancialSubmitted,
                isSecuritySubmitted,
                isAdditionalSubmitted,
              )}
            />
            <ListItem
              className="m-0"
              title="Assigned to"
              value={
                <AssignLenderUserSelect
                  lenderApplicationId={lenderApplicationId}
                  assignedLenderUser={lenderApplication.assignedLenderUser}
                  onSuccess={optimisticUpdate}
                />
              }
            />
          </div>

          <div className="-mx-4 flex items-center justify-end gap-2 px-4 pt-4">
            <Button onClick={goToDetails}>
              <ArrowRight className="size-4" />
            </Button>

            {[
              LenderApplicationsFilterEnum.NO_COMPANY_LOAN_BIDS,
              LenderApplicationsFilterEnum.NO_INVOICE_PURCHASE_BIDS,
              LenderApplicationsFilterEnum.NO_REAL_ESTATE_BIDS,
            ].includes(filter) && (
              <Button variant="danger" onClick={() => setShowRejectModal(true)}>
                <Trash2 className="size-4" />
              </Button>
            )}
          </div>
        </motion.section>
      )}

      {showRejectModal && (
        <RejectApplication
          setOpen={(isOpen: boolean) => setShowRejectModal(isOpen)}
          customerCompany={customerCompany}
          lenderApplicationId={lenderApplicationId}
        />
      )}
    </Fragment>
  );
};
